import type { VNodeChild } from "vue";
import type { RouteLocationRaw } from "vue-router";
import type { Agent, AgentTool, AgentWithDeployments } from "@/schemas/agent.schema";
import omit from "lodash/omit";
import { computed, defineComponent } from "vue";
import { RouterLink, useRouter } from "vue-router";
import { openCopyToProjectsModal } from "@/components/CopyToProjectsModal";
import Popover from "@/components/primitives/popover/Popover";
import ProjectColumn from "@/components/ProjectColumn";
import { openConfirmDelete } from "@/components/providers/AlertDialogProvider";
import { toaster } from "@/components/providers/ToasterProvider";
import Badge from "@/components/ui/badge/Badge";
import Button from "@/components/ui/button/Button";
import {
  useColumns,
  useFilters,
  useSelectedButtons,
} from "@/components/ui/data-table/composables/helpers";
import { useTable } from "@/components/ui/data-table/composables/table";
import DataTable from "@/components/ui/data-table/DataTable";
import { MinimizedTableActions } from "@/components/ui/data-table/helpers/actions";

import { removeSelectedButton } from "@/components/ui/data-table/helpers/buttons";
import { SortedHeader } from "@/components/ui/data-table/helpers/headers";
import { TableSelection } from "@/components/ui/data-table/helpers/select";
import Icon from "@/components/ui/icon/Icon";
import QueryLoader from "@/components/ui/loader/QueryLoader";
import ScrollArea from "@/components/ui/scroll-area/ScrollArea";
import { TableSkeleton } from "@/components/ui/skeleton";
import Tooltip from "@/components/ui/tooltip/Tooltip";
import { isDark } from "@/composables/theme";
import { useResourceLimit } from "@/composables/useResourceLimit";
import McpIcon from "@/icons/svgs/MCP";
import { DEPLOYMENT_CONFIGS } from "@/pages/agents/deploy/deployment-config";
import { useDeleteAgent, useUpdateAgent } from "@/queries/agents.query";
import { useMembersQuery } from "@/queries/members.query";
import { useModelsQuery } from "@/queries/models.query";
import { useTTSVoicesQuery } from "@/queries/tts-voices.query";
import { $t } from "@/services/i18n";
import { getModelIcon, getModelObject } from "@/stores/model.store";
import { useProjectStore } from "@/stores/project.store";
import { hasPermission, useUserProfile } from "@/stores/user-profile.store";
import { cn } from "@/utils/cn";
import { notifyError } from "@/utils/errors";
import { sanitizeMcpServersForExport } from "../form/mcp-servers/mcp-server-transfer";
import ToolGroupsList from "../message-blocks/ToolGroupsList";
import ModelCard from "../ModelCard";
import { openTemplateSelector } from "../templates/components/TemplateSelector";
import { useImportAgent } from "../useImportAgent";
import { useAgentsWithDeployments } from "./composables/useAgentsWithDeployments";
import AgentDeploymentsCell from "./deployments/AgentDeploymentsCell";
import { CALL_ROW_TITLE_KEYS, collectListTools, ESCALATE_ROW_TITLE_KEY, tableToolEntries } from "./tool-families";
import { getFamilyIcon, getToolName } from "./utils";

/**
 * AI-1195: tool chip that opens the grouped list: a popover on a real button,
 * reachable by keyboard with Esc to close.
 */
function renderToolsPopover(tools: AgentTool[], triggerClass: string, label: VNodeChild, ariaLabel?: string) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" aria-label={ariaLabel} class={cn(triggerClass, "cursor-pointer outline-none focus-visible:ring-2")}>
          {label}
        </button>
      </Popover.Trigger>
      <Popover.Content side="top" align="start" collisionPadding={8} class="w-80 max-w-[calc(100vw-1rem)] p-0 text-sm">
        {/* A long list scrolls inside the height collision detection gave the
            popover instead of running off the screen; the fallback only covers
            the first frame before reka measures. */}
        <ScrollArea class="max-h-[min(24rem,calc(var(--reka-popover-content-available-height,24rem)-1rem))]" scrollbarSize="sm">
          <div class="p-3">
            <ToolGroupsList tools={tools} />
          </div>
        </ScrollArea>
      </Popover.Content>
    </Popover.Root>
  );
}

export default defineComponent({
  name: "AgentsTable",
  setup() {
    const { data: agents, agentsQuery } = useAgentsWithDeployments();
    const modelsQuery = useModelsQuery();
    const voicesQuery = useTTSVoicesQuery();
    const deleteMutation = useDeleteAgent();
    const updateMutation = useUpdateAgent();
    const router = useRouter();
    const userProfile = useUserProfile();
    const membersQuery = useMembersQuery();
    const projectStore = useProjectStore();
    const agentCount = computed(() => agents.value?.length);
    const { isAtLimit: isAgentsAtLimit, limitInfo: agentsLimitInfo } = useResourceLimit(
      "agents_per_project",
      agentCount
    );

    // Filtros disponíveis para modelos LLM
    const llmModels = computed(() =>
      (modelsQuery.data.value ?? [])
        .filter((model) => model.type === "text-generation")
        .map((model) => model.name)
    );

    // Filtros disponíveis para modelos TTS
    const ttsModels = computed(() =>
      (modelsQuery.data.value ?? [])
        .filter((model) => model.type === "text-to-speech")
        .map((model) => model.name)
    );

    // Opções de deployment
    const deploymentOptions = Object.entries(DEPLOYMENT_CONFIGS).map(([key, config]) => ({
      value: key,
      label: config.label,
    }));

    function newAgentAddress(agentId: string): RouteLocationRaw {
      return {
        name: "Agent Playground",
        params: { agent_id: agentId, thread_id: "new" },
      };
    }

    // Helper to check if an agent belongs to the current user
    function isOwnAgent(agent: AgentWithDeployments): boolean {
      return agent.user_id === userProfile.user?.user_id;
    }

    // Sort agents with own agents first
    const sortedAgents = computed(() => {
      const data = agents.value ?? [];
      return [...data].sort((a, b) => {
        const aIsMine = isOwnAgent(a) ? 0 : 1;
        const bIsMine = isOwnAgent(b) ? 0 : 1;
        return aIsMine - bIsMine;
      });
    });

    // Função para obter o nome formatado da voz
    function getVoiceName(ttsModel: string, voiceKey: string): string {
      if (!ttsModel || !voiceKey) return voiceKey || "";

      const voices = (voicesQuery.data.value ?? {})[ttsModel] ?? [];
      const voice = voices.find((v) => v.key === voiceKey);

      return voice?.name || voiceKey;
    }

    const columns = useColumns<AgentWithDeployments>(() => [
      TableSelection(),
      {
        accessorKey: "name",
        header: SortedHeader($t("agent")),
        enableGlobalFilter: true,
        accessorFn: (row) => {
          return `${row.name} ${row.description || ""} ${row.id}`.trim();
        },
        size: 350,
        cell: ({ row }) => {
          const agent = row.original;
          const name = agent.name;
          const id = agent.id;
          const description = agent.description;

          return (
            <div class="space-y-1">
              <div class="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <RouterLink
                  to={newAgentAddress(id)}
                  class="whitespace-nowrap font-bold leading-relaxed hover:text-brand-11 hover:underline"
                >
                  {name}
                </RouterLink>

                <button
                  class="cursor-pointer rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground transition-colors hover:bg-accent-3"
                  onClick={() => {
                    navigator.clipboard.writeText(id);
                    toaster.success($t("copied_to_clipboard"));
                  }}
                  title={$t("click_to_copy")}
                >
                  {id}
                </button>
              </div>
              {description && (
                <p class="line-clamp-2 text-xs text-muted-foreground">{description}</p>
              )}
            </div>
          );
        },
      },
      // Project column - only visible in global view with projects:read permission
      ...(projectStore.isGlobalView && hasPermission("projects:read")
        ? [
            {
              id: "project",
              accessorKey: "project_id",
              header: $t("project"),
              size: 180,
              cell: ({ row }: { row: { original: AgentWithDeployments } }) => {
                const agent = row.original;
                return (
                  <ProjectColumn
                    projectId={agent.project_id ?? undefined}
                    resourceType="agent"
                    resourceId={agent.id}
                    onReassign={async (projectId) => {
                      await updateMutation.mutateAsync({
                        id: agent.id,
                        project_id: projectId,
                      } as any);
                    }}
                  />
                );
              },
            },
          ]
        : []),
      // Created by column - shows who created the agent
      ...(hasPermission("members:read")
        ? [
            {
              id: "created_by",
              accessorKey: "user_id",
              header: $t("created_by"),
              size: 160,
              cell: ({ row }: { row: { original: AgentWithDeployments } }) => {
                const agent = row.original;
                const isMe = agent.user_id === userProfile.user?.user_id;
                if (isMe) {
                  return <span class="font-semibold text-foreground">{$t("you")}</span>;
                }
                const user = membersQuery.data.value?.find((m) => m.id === agent.user_id);
                return (
                  <span class="text-muted-foreground">{user?.full_name ?? $t("unknown")}</span>
                );
              },
            },
          ]
        : []),

      {
        accessorKey: "llm_model",
        header: "",
        meta: {
          hide: true,
        },
      },
      {
        accessorKey: "tts_model",
        header: "",
        meta: {
          hide: true,
        },
      },
      {
        accessorKey: "user_id",
        header: "",
        meta: {
          hide: true,
        },
      },
      {
        id: "models",
        header: $t("models"),

        cell: ({ row }) => {
          return (
            <div class="flex flex-col gap-2.5">
              <div class="flex items-center gap-1.5">
                <Badge color="amber" size="xs" class="flex items-center gap-1 font-medium">
                  <Icon icon="lucide:sparkles" class="h-3 w-3" />
                  LLM
                </Badge>
                <ModelCard model={row.original.llm_model} />
              </div>
              <div class="flex items-center gap-1.5">
                <Badge color="cyan" size="xs" class="flex items-center gap-1 font-medium">
                  <Icon icon="lucide:volume-2" class="h-3 w-3" />
                  TTS
                </Badge>
                {row.original.tts_model ? (
                  <div class="flex items-center gap-1.5">
                    <Icon
                      class="h-4 w-4 flex-none opacity-70"
                      icon={getModelIcon(getModelObject(row.original.tts_model))}
                    />
                    {row.original.tts_voice && (
                      <div class="flex items-center gap-1">
                        <span class="text-sm font-medium text-foreground">
                          {getVoiceName(row.original.tts_model, row.original.tts_voice)}
                        </span>
                        <span class="text-xs text-muted-foreground opacity-60">
                          ({getModelObject(row.original.tts_model)?.name || row.original.tts_model})
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <span class="text-sm text-muted-foreground">{$t("none_selected")}</span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "tts_voice",
        accessorFn: (row) => {
          return getVoiceName(row.tts_model, row.tts_voice);
        },
        meta: {
          hide: true,
        },
      },
      {
        accessorKey: "tools",
        header: $t("tools"),
        cell: ({ row }) => {
          // AI-1195: same families as the chat, in group order. An MCP server
          // is one indigo chip with its tool count (hover lists its tools);
          // the full grouped list sits behind the overflow count.
          const allTools = collectListTools(row.original);
          const entries = tableToolEntries(allTools);
          const visibleEntries = entries.slice(0, 3);
          const remainingCount = entries.length - visibleEntries.length;

          if (!allTools.length) {
            return <span class="text-xs text-muted-foreground opacity-50">{$t("none")}</span>;
          }

          const chip = "flex items-center gap-1 whitespace-nowrap rounded p-1 text-xs font-normal leading-none";

          return (
            <ul class="flex flex-wrap gap-1">
              {visibleEntries.map((entry) => {
                if (entry.kind === "mcp_server") {
                  return (
                    <li key={`mcp:${entry.serverKey}`}>
                      {renderToolsPopover(
                        entry.tools,
                        cn(chip, "border border-indigo-6 bg-indigo-3 text-indigo-11 hover:bg-indigo-4 focus-visible:ring-indigo-8"),
                        <>
                          <McpIcon class="h-3.5 w-3.5 shrink-0" />
                          <b class="font-semibold">{entry.serverName}</b>
                          <span class="opacity-75">{$t("agents:tool_families.mcp_tools_count", { count: entry.tools.length })}</span>
                        </>,
                      )}
                    </li>
                  );
                }
                const { family, tool } = entry;
                let label = getToolName(tool);
                if (family.kind === "call_control") label = $t(CALL_ROW_TITLE_KEYS[family.action]);
                else if (family.kind === "escalation") label = $t(ESCALATE_ROW_TITLE_KEY);
                return (
                  <li key={`tool:${tool.type}:${tool.tool.name}`} class={cn(chip, "cursor-default bg-muted")}>
                    <Icon
                      icon={getFamilyIcon(family)}
                      class={cn(
                        "h-4 w-4",
                        family.kind === "call_control" && "text-violet-11",
                        family.kind === "escalation" && "text-teal-11",
                        family.kind !== "call_control" && family.kind !== "escalation" && "opacity-70",
                      )}
                    />
                    <span class="whitespace-nowrap">{label}</span>
                  </li>
                );
              })}
              {remainingCount > 0 && (
                <li>
                  {renderToolsPopover(
                    allTools,
                    cn(chip, "bg-muted hover:bg-accent-3"),
                    <>+{remainingCount}</>,
                    $t("agents:tool_families.show_all_tools", { count: remainingCount }),
                  )}
                </li>
              )}
            </ul>
          );
        },
      },
      {
        id: "deployments",
        header: $t("deployments"),
        accessorFn: (row) => {
          const deployments = [] as string[];
          if (row.deployments.whatsapp) deployments.push("whatsapp");
          if (row.deployments.whatsappBaileys?.phone_number) deployments.push("whatsapp_baileys");
          if (row.deployments.chatWidget) deployments.push("chat_widget");
          if (row.deployments.sip) deployments.push("sip");
          if (row.deployments.telegram) deployments.push("telegram");
          return deployments;
        },

        cell: ({ row }) => {
          const agent = row.original as AgentWithDeployments;
          return <AgentDeploymentsCell agent={agent} />;
        },
      },

      MinimizedTableActions([
        // Ações Primárias (mais usadas)
        {
          icon: "lucide:message-circle-more",
          label: "Playground",
          description: $t("agents:actions.open_chat.description"),
          handler: (agent) => {
            router.push(newAgentAddress(agent.id));
          },
        },
        {
          icon: "lucide:pencil",
          label: $t("agents:actions.edit.title"),
          description: $t("agents:actions.edit.description"),
          hide: (agent: AgentWithDeployments) => {
            // Full permission can edit any agent
            if (hasPermission("agents:update")) return false;
            // Own permission can only edit their own agents
            if (hasPermission("agents:update:own")) {
              return agent.user_id !== userProfile.user?.user_id;
            }
            // No permission, hide
            return true;
          },
          handler: (agent) => {
            router.push({
              name: "Edit Agent",
              params: { agent_id: agent.id, tab: "profile" },
            });
          },
        },

        // Ações de Deploy/Produção
        {
          icon: "lucide:rocket",
          label: $t("common:deployments"),
          description: $t("agents:actions.deploy.description"),
          handler: (agent) => {
            router.push(`/agents/deploy/${agent.id}`);
          },
        },

        {
          icon: "lucide:message-square-text",
          label: $t("agents:conversations"),
          description: $t("agents:select_conversation"),
          handler: (agent) => {
            router.push({
              name: "Agent Conversations",
              params: { agent_id: agent.id },
            });
          },
        },

        // Ações Secundárias
        {
          icon: "lucide:upload",
          label: $t("agents:actions.export.title"),
          description: $t("agents:actions.export.description"),
          handler: exportAgent,
        },
        {
          icon: "lucide:copy",
          label: $t("copy_to_projects"),
          description: $t("copy_to_projects_description"),
          hide: () => !hasPermission("agents:create"),
          handler: (agent: AgentWithDeployments) => {
            openCopyToProjectsModal({
              resourceType: "agent",
              resourceId: agent.id,
              resourceName: agent.name,
              sourceProjectId: agent.project_id ?? undefined,
              onSuccess: () => void agentsQuery.refetch(),
            });
          },
        },

        // Separador antes de ações destrutivas
        "separator",

        // Ações Destrutivas
        {
          icon: "lucide:trash-2",
          label: $t("agents:actions.remove.title"),
          description: $t("agents:actions.remove.description"),
          hide: (agent: AgentWithDeployments) => {
            // Full permission can delete any agent
            if (hasPermission("agents:delete")) return false;
            // Own permission can only delete their own agents
            if (hasPermission("agents:delete:own")) {
              return agent.user_id !== userProfile.user?.user_id;
            }
            // No permission, hide
            return true;
          },
          handler: (agent) => {
            openConfirmDelete(async (close) => {
              await deleteMutation.mutateAsync(agent.id).catch(notifyError);

              close();
              toaster.success(
                $t("agents:feedbacks.delete_agent_success", {
                  name: agent.name,
                })
              );
            });
          },
        },
      ]),
    ]);

    const filters = useFilters(() => [
      {
        _type: "boolean",
        accessorKey: "user_id",
        label: $t("created_by_me"),
        transform: () => userProfile.user?.user_id,
      },
      {
        _type: "list",
        accessorKey: "llm_model",
        label: $t("llm_model"),
        mode: "single",
        options: llmModels.value,
      },
      {
        _type: "list",
        accessorKey: "tts_model",
        label: $t("tts_model"),
        mode: "single",
        options: ttsModels.value,
      },
      {
        _type: "string",
        accessorKey: "tts_voice",
        label: $t("voice"),
      },
      {
        _type: "list",
        accessorKey: "deployments",
        label: $t("deployments"),
        mode: "multiple",
        filterFunction: (rowValue: any, selectedValues: any) => {
          if (!Array.isArray(rowValue) || !Array.isArray(selectedValues)) return false;
          // Lógica AND: o agente deve ter TODOS os deployments selecionados
          return selectedValues.every((selectedDeploy: any) => rowValue.includes(selectedDeploy));
        },
        options: deploymentOptions.map((opt) => ({
          value: opt.value,
          label: opt.label,
        })),
      },
    ]);

    const table = useTable({
      data: sortedAgents,
      columns,
    });

    // Only show bulk delete for users with full agents:delete permission
    // Users with only agents:delete:own should use individual delete actions
    const selectedButtons = useSelectedButtons(() =>
      hasPermission("agents:delete")
        ? [
            removeSelectedButton((btns: Agent[]) => {
              openConfirmDelete(async (close) => {
                for (const btn of btns) {
                  await deleteMutation
                    .mutateAsync(btn.id)
                    .then(() => {
                      toaster.success(
                        $t("agents:feedbacks.delete_agent_success", {
                          name: btn.name,
                        })
                      );
                    })
                    .catch((e) => {
                      notifyError(e);
                    });
                }
                table.resetRowSelection();
                close();
              });
            }),
          ]
        : []
    );

    const { importAgent } = useImportAgent({
      setAgent: (agent) => {
        router.push({
          name: "Create Agent",
          params: { tab: "profile" },
          state: {
            agent: agent as any,
          },
        });
      },
    });

    return () => (
      <QueryLoader
        queries={[
          agentsQuery,
          modelsQuery,
          voicesQuery,
          membersQuery,
        ]}
      >
        <QueryLoader.Loading>
          <TableSkeleton rows={10} columns={5} />
        </QueryLoader.Loading>
        <DataTable.Root table={table}>
          <DataTable.Toolbar
            globalFilter
            selectedButtons={selectedButtons.value}
            filters={filters.value}
          >
            {{
              $right: () => (
                <div class="center gap-2">
                  {hasPermission("agents:create") && (
                    <Tooltip
                      text={
                        isAgentsAtLimit.value
                          ? $t("subscription-limits:limit_reached_tooltip", {
                              limit: agentsLimitInfo.value?.limit,
                              resource: $t("subscription-limits:agents_per_project"),
                            })
                          : undefined
                      }
                    >
                      <Button
                        class="flex-none"
                        onClick={importAgent}
                        variant={"secondary"}
                        label={$t("import")}
                        icon={"lucide:download"}
                        disabled={isAgentsAtLimit.value}
                      />
                    </Tooltip>
                  )}

                  {hasPermission("agents:create") && (
                    <Tooltip
                      text={
                        isAgentsAtLimit.value
                          ? $t("subscription-limits:limit_reached_tooltip", {
                              limit: agentsLimitInfo.value?.limit,
                              resource: $t("subscription-limits:agents_per_project"),
                            })
                          : undefined
                      }
                    >
                      <Button
                        class="flex-none"
                        onClick={() => openTemplateSelector()}
                        variant={isDark.value ? "light" : "filled"}
                        color="green"
                        label={$t("agents:create_agent")}
                        icon={"lucide:plus"}
                        disabled={isAgentsAtLimit.value}
                      />
                    </Tooltip>
                  )}
                </div>
              ),
            }}
          </DataTable.Toolbar>
          <DataTable.Content class="text-sm" density={"comfortable"}></DataTable.Content>
          <DataTable.Pagination></DataTable.Pagination>
        </DataTable.Root>
      </QueryLoader>
    );
  },
});

export function exportAgent(agent: Agent) {
  // create a download to a json file with the agent form without its id
  const json = omit(agent, [
    "id",
    "organization_id",
    "created_at",
    "updated_at",
    "user_id",
    "share",
    "agent_deploy_whatsapp",
    "agent_deploy_chat_widget",
    "agent_deploy_sip",
    "deployments",
  ]);

  // AI-1179: an MCP connection reference is only meaningful inside the source
  // project and credentials never travel in the export.
  const exported = {
    ...json,
    mcp_servers: json.mcp_servers?.length
      ? sanitizeMcpServersForExport(json.mcp_servers)
      : json.mcp_servers,
  };

  const blob = new Blob([JSON.stringify(exported)], {
    type: "application/json",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${agent.name}.json`;
  a.click();
}
